'use client';
import { FeedResponse, TagCounter } from "@/lib/models/feedResponse";
import { PostDetail } from "@/lib/services/postService";
import { Utils } from "@/lib/utils";
import { Project } from "@prisma/client";
import { useRouter, useSearchParams } from "next/navigation";
import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";

type Filter = {
      tag?: string;
      page: number;
}


type FeedContextType = {
      project: Project | null;
      posts: PostDetail[];
      tags: TagCounter[];
      filter: Filter | undefined;
      total: number;
      totalPages: number;
      loading: boolean;
      hasMore: boolean;
      loadMore: () => void;
      changeFilter: (filter: Omit<Filter, 'page'>) => void;
}

export const FeedContext = createContext<FeedContextType | undefined>(undefined);

type FeedProviderProps = {
      children: ReactNode;
      initialProject: Project;
}

export function FeedProvider({ children, initialProject }: FeedProviderProps) {
      const router = useRouter();
      const searchParams = useSearchParams();
      const [currentProject] = useState<Project | null>(initialProject ?? null);
      const [posts, setPosts] = useState<PostDetail[]>([]);
      const [total, setTotal] = useState(0);
      const filterRef = useRef<Filter>({
            page: 0,
            tag: searchParams.get('tag') ?? 'all'
      });
      const [totalPages, setTotalPages] = useState(1);
      const [loading, setLoading] = useState(false);
      const [hasMore, setHasMore] = useState(true);
      const [tags, setTags] = useState<TagCounter[]>([]);

      const fetchPosts = useCallback((resetPage: boolean = false) => {
            const params = new URLSearchParams();
            params.set('page', resetPage ? '1' : (filterRef.current?.page.toString() ?? '1'));
            params.set('length', '10');
            if (filterRef.current?.tag && filterRef.current?.tag !== 'all') {
                  params.set('tag', filterRef.current.tag);
            }


            setLoading(true);
            Utils.http.fetchData<FeedResponse>(`/api/p/${currentProject?.slug}/news?${params.toString()}`)
                  .then(response => {
                        if (resetPage) {
                              setPosts(response.posts);
                        } else {
                              setPosts(prev => [...prev, ...response.posts]);
                        }
                        setTotal(response.total);
                        setTotalPages(response.totalPages);
                        setTags(response.tags);

                        if (response.posts.length < 10) {
                              setHasMore(false);
                        }
                  }).catch(error => {
                        console.error("Error fetching posts:", error);
                  }).finally(() => {
                        setLoading(false);

                  });
      }, [currentProject, filterRef.current]);



      const loadMore = () => {
            filterRef.current = { ...filterRef.current, page: filterRef.current.page + 1 };
            fetchPosts();
      };

      const changeFilter = (filter: Omit<Filter, 'page'>) => {
            filterRef.current = { ...filterRef.current, ...filter, page: 1 };

            const params = new URLSearchParams(searchParams.toString());
            if (filter.tag && filter.tag !== 'all') {
                  params.set('tag', filter.tag);
            } else {
                  params.delete('tag');
            }
            router.push(`?${params.toString()}`);

            fetchPosts(true);
      };

      const contextValue = useMemo(() => ({
            posts,
            total,
            totalPages,
            tags,
            project: currentProject,
            loading,
            hasMore,
            filter: filterRef.current,
            loadMore,
            changeFilter
      }), [posts, total, filterRef.current, tags, totalPages, currentProject, loading, hasMore, loadMore, changeFilter]);

      return (<FeedContext.Provider value={contextValue}>
            {children}
      </FeedContext.Provider>
      );
}

export const useFeed = () => {
      const context = useContext(FeedContext);
      if (!context) throw new Error('useFeed must be used within a FeedProvider');
      return context;
};

